/**
 * Färghjälp för företagens profilfärger.
 *
 * Ett företag levererar en hexkod ur sin logga. Vi vet ingenting om den i
 * förväg – den kan vara nästan svart eller nästan gul. Texten ovanpå måste
 * ändå gå att läsa i solsken utanför en butik, och det är inget vi kan
 * överlåta till att någon ögnar av det i adminytan.
 *
 * Därför räknas kontrasten fram i stället för att väljas. Samma matematik som
 * i kontrasttestet av designsystemet, WCAG 2.1.
 */

/** Tolkar #abc, #aabbcc och abc. Returnerar null när strängen inte är en färg. */
export function parseHexColor(value: string | null | undefined): [number, number, number] | null {
  if (!value) return null
  const clean = value.trim().replace(/^#/, '')
  if (!/^[0-9a-fA-F]{3}$|^[0-9a-fA-F]{6}$/.test(clean)) return null

  const full =
    clean.length === 3
      ? clean
          .split('')
          .map((c) => c + c)
          .join('')
      : clean

  return [
    parseInt(full.slice(0, 2), 16),
    parseInt(full.slice(2, 4), 16),
    parseInt(full.slice(4, 6), 16),
  ]
}

/** Normaliserad form, så att "abc" och "#AABBCC" sparas likadant. */
export function normalizeHexColor(value: string | null | undefined): string | null {
  const rgb = parseHexColor(value)
  if (!rgb) return null
  return '#' + rgb.map((channel) => channel.toString(16).padStart(2, '0')).join('')
}

/** Relativ luminans enligt WCAG 2.1. */
export function luminance(hex: string): number {
  const rgb = parseHexColor(hex)
  if (!rgb) return 0
  const channels = rgb.map((value) => {
    const c = value / 255
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4
  })
  return 0.2126 * channels[0] + 0.7152 * channels[1] + 0.0722 * channels[2]
}

export function contrastRatio(a: string, b: string): number {
  const la = luminance(a)
  const lb = luminance(b)
  const [light, dark] = la > lb ? [la, lb] : [lb, la]
  return (light + 0.05) / (dark + 0.05)
}

/** Bläcket vi har att välja mellan. Samma två som resten av appen använder. */
const INK_DARK = '#131320'
const INK_LIGHT = '#ffffff'

/**
 * Vilken textfärg som håller mot en bakgrund.
 *
 * Väljer den av de två som ger högst kontrast. Det ger alltid ett svar, även
 * mot en mellanton där ingendera når 4,5:1 – och för de fallen finns
 * `hasReadableText` nedan, som säger ifrån.
 */
export function readableInkOn(background: string): 'light' | 'dark' {
  return contrastRatio(background, INK_LIGHT) >= contrastRatio(background, INK_DARK)
    ? 'light'
    : 'dark'
}

/**
 * Om någon av textfärgerna faktiskt duger mot bakgrunden.
 *
 * Mellantoner är problemet. En medelgrå eller dov turkos ger under 4,5:1 mot
 * både vitt och nästan svart, och då finns ingen textfärg som räddar den –
 * företaget måste välja en mörkare eller ljusare ton. Adminytan varnar hellre
 * direkt än låter en oläslig kupong gå ut i häftet.
 */
export function hasReadableText(background: string, minimum = 4.5): boolean {
  return (
    Math.max(contrastRatio(background, INK_LIGHT), contrastRatio(background, INK_DARK)) >= minimum
  )
}

/**
 * En mörkare variant av färgen, för lutningen på stora ytor.
 *
 * Designregeln säger att en plan mättad färg över en stor yta läser som
 * affisch medan en lutning läser som material. Samma resonemang gäller
 * företagens färger som Lirablå.
 */
export function darken(hex: string, amount = 0.22): string {
  const rgb = parseHexColor(hex)
  if (!rgb) return hex
  return (
    '#' +
    rgb
      .map((channel) => Math.round(channel * (1 - amount)))
      .map((channel) => Math.max(0, Math.min(255, channel)).toString(16).padStart(2, '0'))
      .join('')
  )
}

/** Lutningen som används på företagets kupongsidor och omslag. */
export function brandGradient(hex: string): string {
  return `linear-gradient(157deg, ${hex} 0%, ${darken(hex, 0.14)} 46%, ${darken(hex, 0.3)} 100%)`
}
